import { supabase } from "../lib/supabase";
import { ContainerDocumentType } from "../utils/containerDocumentsUtils";

/**
 * Container document interface
 */
export interface ContainerDocument {
  id: number;
  container_id: number;
  document_type: ContainerDocumentType;
  file_path: string;
  file_name: string;
  file_size: number | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

/**
 * Create a container document record in the database
 */
export const createContainerDocument = async (
  containerId: number,
  documentType: ContainerDocumentType,
  filePath: string,
  fileName: string,
  fileSize?: number
) => {
  try {
    // Get current user for created_by
    const {
      data: { user },
    } = await supabase.auth.getUser();

    console.log("Creating container document record with:", {
      container_id: containerId,
      document_type: documentType,
      file_path: filePath,
      file_name: fileName,
      file_size: fileSize,
      created_by: user?.id,
    });

    const { data, error } = await supabase
      .from("container_documents")
      .insert([
        {
          container_id: containerId,
          document_type: documentType,
          file_path: filePath,
          file_name: fileName,
          file_size: fileSize || null,
          created_by: user?.id || null,
        },
      ])
      .select()
      .single();

    if (error) {
      console.error("Database error during insert:", error);
      throw error;
    }

    return {
      success: true,
      data,
      error: null,
    };
  } catch (error) {
    console.error("Error creating container document:", error);
    return {
      success: false,
      data: null,
      error,
    };
  }
};

/**
 * Get all documents for a container, optionally filtered by type
 */
export const getContainerDocuments = async (
  containerId: number,
  documentType?: ContainerDocumentType
) => {
  try {
    console.log(
      `Fetching container documents for container ID: ${containerId}`,
      documentType ? `, type: ${documentType}` : ""
    );

    let query = supabase
      .from("container_documents")
      .select("*")
      .eq("container_id", containerId)
      .order("created_at", { ascending: false });

    if (documentType) {
      query = query.eq("document_type", documentType);
    }

    const { data, error } = await query;

    if (error) {
      console.error("Database error during select:", error);
      throw error;
    }

    console.log(`Found ${data?.length || 0} container documents`);
    return {
      success: true,
      data: data as ContainerDocument[],
      error: null,
    };
  } catch (error) {
    console.error("Error fetching container documents:", error);
    return {
      success: false,
      data: [] as ContainerDocument[],
      error,
    };
  }
};

/**
 * Delete a container document (database record and file)
 */
export const deleteContainerDocument = async (documentId: number) => {
  try {
    // Get the document record first to get the file path
    const { data: document, error: fetchError } = await supabase
      .from("container_documents")
      .select("file_path")
      .eq("id", documentId)
      .single();

    if (fetchError) {
      console.error("Database error during fetch:", fetchError);
      throw fetchError;
    }

    // Delete from database
    const { error: deleteError } = await supabase
      .from("container_documents")
      .delete()
      .eq("id", documentId);

    if (deleteError) {
      console.error("Database error during delete:", deleteError);
      throw deleteError;
    }

    // Delete file from storage if we have a file path
    if (document?.file_path) {
      const cleanPath = document.file_path.trim().replace(/^\//, "");
      const { error: storageError } = await supabase.storage
        .from("container_documents")
        .remove([cleanPath]);

      if (storageError) {
        console.error(
          "Error deleting document file from storage:",
          storageError
        );
      }
    }

    return {
      success: true,
      error: null,
    };
  } catch (error) {
    console.error("Error deleting container document:", error);
    return {
      success: false,
      error,
    };
  }
};

